import React, { useState, useEffect } from "react";
import Step1PersonalInfo from "./steps/Step1PersonalInfo";
import Step3MedicalInfo from "./steps/Step3MedicalInfo";
import Step4PlanInfo from "./steps/Step4PlanInfo";
import Step4Dependants from "./steps/Step4Dependants";
import Step5BankingDetails from "./steps/Step5BankingDetails";
import Step5Consent from "./steps/Step5Consent";
import Step6PreviousSchemes from "./steps/Step6PreviousSchemes";
import Step7MedicalHistory from "./steps/Step7MedicalHistory";
import Step6SubmissionTime from "./steps/Step6SubmissionTime";
import Step7Confirmation from "./steps/Step7Confirmation";
import { 
  Button, 
  Stepper, 
  Step, 
  StepLabel, 
  Container, 
  Paper, 
  Box, 
  useTheme,
  useMediaQuery,
  Typography,
  ThemeProvider,
  CssBaseline,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from "@mui/material";
import axios from "axios";
import theme from '../theme/theme';
import { sharedStyles } from './styles/sharedStyles';
import { submitAgileLead, submitBonitasApplication, saveFormSubmission } from '../services/apiService';
import { getListId } from '../utils/listIdConfig';
import { mapToOnlineAppPayload } from '../utils/onlineAppPayloadMapper';

const steps = [
  "Choose Plan",
  "Personal Information",
  "Contact Details",
  "Dependants Registration",
  "Banking Details",
  "Medical History Details"
];

const getFromLocalStorage = () => {
  const data = localStorage.getItem("formData");
  return data ? JSON.parse(data) : {};
};

const saveToLocalStorage = (data) => {
  localStorage.setItem("formData", JSON.stringify(data));
};

// Mapping function for BonCap: Converts to DPM JSON structure
function mapToBonCapDPMPayload(formData) {
  const get = (obj, key, fallback = null) => (obj && obj[key] !== undefined ? obj[key] : fallback);
  
  // Helper to convert dates
  const convertDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).getTime();
  };
  
  // Helper to convert date string format
  const formatDateString = (dateStr) => {
    if (!dateStr) return null;
    return dateStr; // Keep as YYYY-MM-DD
  };
  
  // Build persons array
  const persons = [];
  const mainMember = {
    identification: {
      idTypeCode: formData.idpasstype === 'ID Document' ? 'SID' : 'PASSPORT',
      idNumber: formData.mainid_number || '',
      idCountryCode: formData.idpasstype === 'ID Document' ? 'ZA' : null
    },
    dependentNumber: '00',
    associationCode: '1',
    relationshipTypeCode: 'SELF',
    title: formData.main_title || null,
    firstName: formData.main_firstname || null,
    lastName: formData.main_surname || null,
    gender: formData.mgender_radio === 'Male' ? 'M' : (formData.mgender_radio === 'Female' ? 'F' : null),
    dateOfBirth: formatDateString(formData.m_dob),
    language: formData.main_lang || null,
    ethnicity: formData.m_race || null,
    maritalStatus: formData.main_marital || null,
    contacts: [],
    conditions: [],
    schemeHistory: [],
    employmentDetails: formData.recommendedMedicalAidPlan === 'PL470' ? [{
      startDate: null,
      endDate: null,
      incomeBracketCategoryCode: null,
      incomeAmount: formData.incomeAmount ? parseFloat(formData.incomeAmount) : null,
      employmentNumber: null
    }] : [],
    documents: [],
    emailAddress: null,
    mobileNumber: null,
    receiveHardCopyCard: false,
    generalWaitingPeriodIncPMBIndicator: true,
    pecWaitingPeriodIncPMBIndicator: false,
    overrideGeneralWaitingPeriod: false,
    overridePreExistingConditionWaitingPeriod: false,
    isExistingIndicator: false
  };
  
  // Add contacts if available
  // Contacts should include: PP (personal phone), WE (work email), PE (personal email)
  const contacts = Array.isArray(formData.members_contacts) && formData.members_contacts.length > 0 ? formData.members_contacts[0] : {};
  const cellNumber = contacts.m_cell || formData.m_cell || '';
  const emailAddress = contacts.m_email || formData.m_email || '';
  
  if (cellNumber) {
    mainMember.contacts.push({
      contactTypeCode: 'PP',
      contactValue: cellNumber,
      isPreferred: false
    });
    mainMember.mobileNumber = cellNumber;
  }
  
  if (emailAddress) {
    // Work email
    mainMember.contacts.push({
      contactTypeCode: 'WE',
      contactValue: emailAddress,
      isPreferred: false
    });
    // Personal email (same as work email if only one provided)
    mainMember.contacts.push({
      contactTypeCode: 'PE',
      contactValue: emailAddress,
      isPreferred: false
    });
    mainMember.emailAddress = emailAddress;
  }
  
  // Add medical conditions
  // BonCap uses specific question codes: 66-74
  if (Array.isArray(formData.medical_history) && formData.medical_history.length > 0) {
    mainMember.conditions = formData.medical_history.map(cond => {
      // Map question to BonCap question codes
      let questionCode = null;
      if (cond.question) {
        // Map old question format to new BonCap codes
        // q1 -> 66, q2 -> 67, etc. based on mapping rules
        const questionNumber = parseInt(cond.question.replace('q', '')) || 0;
        // Map to BonCap question codes (66-74)
        questionCode = String(66 + (questionNumber - 1)); // Start from 66
      }
      
      // Medication should be an object with drugCode and drugName, not a string
      let medication = null;
      if (cond.medication) {
        // If medication is already an object, use it; otherwise create object from string
        if (typeof cond.medication === 'object' && cond.medication.drugCode) {
          medication = {
            drugCode: cond.medication.drugCode || null,
            drugName: cond.medication.drugName || cond.medication.drugCode || null
          };
        } else if (typeof cond.medication === 'string' && cond.medication.trim()) {
          // Convert string medication to object format
          medication = {
            drugCode: cond.medication.trim(),
            drugName: cond.medication.trim()
          };
        }
      }
      
      return {
        questionCode: questionCode,
        conditionCode: cond.illness || null,
        firstTreatmentDate: cond.firsttreatmentdate ? convertDate(cond.firsttreatmentdate) : null,
        lastTreatmentDate: cond.lasttreatmentdate ? convertDate(cond.lasttreatmentdate) : null,
        provider: cond.gpfullname ? {
          providerCode: cond.gpfullname,
          providerName: cond.gpfullname
        } : null,
        medication: medication,
        isCurrent: true
      };
    });
  }
  
  // Add scheme history
  if (Array.isArray(formData.schemeHistory) && formData.schemeHistory.length > 0) {
    mainMember.schemeHistory = formData.schemeHistory.map(sh => ({
      scheme: {
        schemeCode: null,
        schemeName: sh.schemeName || null
      },
      membershipNumber: sh.membershipNumber || null,
      associationCode: null,
      startDate: sh.startDate ? convertDate(sh.startDate) : null,
      endDate: sh.endDate ? convertDate(sh.endDate) : null
    }));
  }
  
  persons.push(mainMember);
  
  // Build GP nominations at membership level (not inside person)
  const nominatedFamilyProviders = [];
  if (Array.isArray(formData.gpnominations)) {
    formData.gpnominations.forEach(gp => {
      if (gp.providerCode || gp.providerName) {
        nominatedFamilyProviders.push({
          providerCode: gp.providerCode || null,
          providerName: gp.providerName || null
        });
      }
    });
  }
  
  // Helper to map account type to BonCap codes
  const mapAccountType = (accountType) => {
    if (!accountType) return 'CA';
    // If already a code (CA, SA, TA, BA, SSA), return as is
    if (['CA', 'SA', 'TA', 'BA', 'SSA'].includes(accountType)) return accountType;
    // Map labels to codes
    const mapping = {
      'Cheque Account or Current Account': 'CA',
      'Savings Account': 'SA',
      'Transmission Account': 'TA',
      'Bond Account': 'BA',
      'Subscription Share account': 'SSA',
      'Current': 'CA',
      'Savings': 'SA',
      'Transmission': 'TA',
      'Bond': 'BA'
    };
    return mapping[accountType] || 'CA';
  };
  
  // Build bank details
  const bankDetails = [];
  
  // Contribution bank
  if (formData.bankApiName && formData.accountNumber) {
    bankDetails.push({
      usage: 'A',
      bankCode: formData.bankApiName,
      bankAccountType: mapAccountType(formData.accountType),
      branchCode: formData.branchCode || '',
      accountNumber: formData.accountNumber,
      accountName: formData.accountHolder || (formData.main_firstname + ' ' + formData.main_surname).trim(),
      debitOrderDay: parseInt(formData.debitOrderDate) || 1,
      isThirdPartyAccount: formData.isThirdPartyAccount || false
    });
  }
  
  // Refund bank (or same as contribution)
  if (formData.refundsSameAsContribution === 'different' && formData.refundBankApiName && formData.refundAccountNumber) {
    bankDetails.push({
      usage: 'Z',
      bankCode: formData.refundBankApiName,
      bankAccountType: mapAccountType(formData.refundAccountType),
      branchCode: formData.refundBranchCode || '',
      accountNumber: formData.refundAccountNumber,
      accountName: formData.refundAccountHolder || (formData.main_firstname + ' ' + formData.main_surname).trim(),
      debitOrderDay: parseInt(formData.debitOrderDate) || 1,
      isThirdPartyAccount: formData.isThirdPartyAccount || false
    });
  } else if (formData.bankApiName && formData.accountNumber) {
    // Same as contribution bank
    bankDetails.push({
      usage: 'Z',
      bankCode: formData.bankApiName,
      bankAccountType: mapAccountType(formData.accountType),
      branchCode: formData.branchCode || '',
      accountNumber: formData.accountNumber,
      accountName: formData.accountHolder || (formData.main_firstname + ' ' + formData.main_surname).trim(),
      debitOrderDay: 1,
      isThirdPartyAccount: formData.isThirdPartyAccount || false
    });
  }
  
  // Build addresses
  // Address structure matches example: suburb, cityTownCode, postalCode, poBoxNumber
  const addresses = [];
  if (formData.m_psuburb || formData.m_postalcode || formData.mainid_pobox) {
    addresses.push({
      addressTypeCode: 'P',
      suburb: formData.m_psuburb || null,
      cityTownCode: formData.m_psnum || null,
      postalCode: formData.m_postalcode || null,
      poBoxNumber: formData.mainid_pobox || null
    });
  }
  
  // Generate application reference number
  // Format: BON-PL470-<start-date>-<intermediary code>-<ID-no>
  // Note: Uses PL470 in reference number even though optionCode is BCAP
  const getApplicationReferenceNumber = () => {
    const startDate = formData.medstart_date || new Date().toISOString().split('T')[0];
    const formattedStartDate = startDate; // Keep dashes: 2026-01-01
    const intermediaryCode = formData.intermediatoryCode || 'DPP'; // Direct Paying Parent
    const idNumber = formData.mainid_number || '';
    
    return `BON-PL470-${formattedStartDate}-${intermediaryCode}-${idNumber}`;
  };
  
  // Get current date as string for applicationDate
  const getApplicationDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Ensure persons array always has at least one person
  if (persons.length === 0) {
    throw new Error('At least one person (main member) is required');
  }
  
  // Build final payload matching exact structure
  const payload = {
    applicationTypeCode: 'NM',
    applicationReferenceNumber: getApplicationReferenceNumber(),
    scheme: {
      schemeCode: 'BON',
      schemeName: 'BONITAS'
    },
    intermediatory: {
      intermediatoryCode: formData.intermediatoryCode || 'CBU6ABS',
      intermediatoryName: formData.intermediatoryName || 'NO BROKER'
    },
    employerGroup: {
      employerGroupCode: 'BON996DPP',
      employerGroupName: 'DIRECT PAYING MEMBERS'
    },
    membership: {
      membershipReferenceNumber: formData.mainid_number || null,
      principalLastName: formData.main_surname || null,
      optionCode: 'BCAP',
      startDate: formData.medstart_date || getApplicationDate(), // String format: "2026-01-01"
      applicationDate: getApplicationDate(), // String format: "2025-10-21"
      taxNumber: formData.taxNumber || null,
      bankDetails: bankDetails, // Always an array (empty if no banks)
      persons: persons, // Always has at least main member
      addresses: addresses, // Always an array (empty if no address)
      nominatedFamilyProviders: nominatedFamilyProviders, // Always an array
      documents: [] // Always empty array
    },
    created: Date.now(), // Timestamp number
    createdBy: formData.createdBy || 'Bonitas Join Now Form'
  };
  
  return payload;
}

// Mapping function: Converts flat formData to the required API structure with all sections and nulls for missing fields
function mapToBonitasPayload(formData) {
  // Helper to get value or null
  const get = (obj, key, fallback = null) => (obj && obj[key] !== undefined ? obj[key] : fallback);

  // Advisor section
  const advisor = {
    lead_id: get(formData, 'leadId', null),
    pre_underwriting: get(formData, 'preUnderwriting', null),
    medstart_date: get(formData, 'medstart_date', null),
    capturedby: null,
    brokercode: null,
    brokeragename: null,
    brokername: null,
    brokeremail: null,
    brokertel: null,
    paypointcode: null,
    start_time: get(formData, 'startTime', null),
    end_time: get(formData, 'endTime', null),
    policy_option: get(formData, 'recommendedMedicalAidPlan', null),
    lead_submit: get(formData, 'leadSubmit', null),
    app_type: get(formData, 'appType', 'individual'),
    advisor_memfullname: null,
    medical_question_one: formData.q1 === 'Yes' ? 'Y' : formData.q1 === 'No' ? 'N' : null,
    medical_question_two: formData.q2 === 'Yes' ? 'Y' : formData.q2 === 'No' ? 'N' : null,
    medical_question_three: formData.q3 === 'Yes' ? 'Y' : formData.q3 === 'No' ? 'N' : null,
    medical_question_four: formData.q4 === 'Yes' ? 'Y' : formData.q4 === 'No' ? 'N' : null
  };

  // --- BANKS ---
  // Always reconstruct banks_contributions and banks_refunds from formData fields if missing or empty
  let banks_contributions = Array.isArray(formData.banks_contributions) && formData.banks_contributions.length > 0
    ? formData.banks_contributions
    : [
      {
        thirdpartycheck: formData.isThirdPartyAccount ? 'third_party' : null,
        bankname: formData.bankApiName || '',
        otherbankname: null,
        accholderid: formData.accountHolderId || formData.mainid_number || '',
        accholder: formData.accountHolder || formData.main_firstname + ' ' + formData.main_surname || '',
        accholdersurname: null,
        accnum: formData.accountNumber || '',
        acctype: formData.accountType || '',
        debitdate: formData.debitOrderDate || '',
        branchcode: formData.branchCode || '',
        branchname: formData.branchName || '',
        verification: null,
        idvalidation: null,
        consentupload: null,
        company_bank: null
      }
    ];
  let banks_refunds = Array.isArray(formData.banks_refunds) && formData.banks_refunds.length > 0
    ? formData.banks_refunds
    : [
      {
        thirdpartycheck: formData.isThirdPartyAccount ? 'third_party' : null,
        sameacccheck: formData.refundsSameAsContribution === 'different' ? 'different_accounts' : 'same_accounts',
        bankname: (formData.refundsSameAsContribution === 'different' ? formData.refundBankApiName : formData.bankApiName) || '',
        otherbankname: null,
        accholderid: (formData.refundsSameAsContribution === 'different' ? formData.refundAccountHolderId : formData.accountHolderId) || formData.mainid_number || '',
        accholder: (formData.refundsSameAsContribution === 'different' ? formData.refundAccountHolder : formData.accountHolder) || formData.main_firstname + ' ' + formData.main_surname || '',
        accholdersurname: null,
        accnum: (formData.refundsSameAsContribution === 'different' ? formData.refundAccountNumber : formData.accountNumber) || '',
        acctype: (formData.refundsSameAsContribution === 'different' ? formData.refundAccountType : formData.accountType) || '',
        debitdate: formData.debitOrderDate || '',
        branchcode: (formData.refundsSameAsContribution === 'different' ? formData.refundBranchCode : formData.branchCode) || '',
        branchname: (formData.refundsSameAsContribution === 'different' ? formData.refundBranchName : formData.branchName) || '',
        verification: null,
        idvalidation: null,
        consentupload: null
      }
    ];

  // Boncap incomes (from Step4PlanInfo)
  let boncap_incomes = [];
  if (formData.recommendedMedicalAidPlan === 'PL470' && formData.incomeAmount) {
    boncap_incomes = [ { income_amount: formData.incomeAmount } ];
  }

  // Dependents (from Step4Dependants)
  const dependents = Array.isArray(formData.dependants) ? formData.dependants.map(dep => ({
    dep_title: dep.d_title || null,
    d_idpass: dep.d_idnum || null,
    d_idvalidation: null,
    d_firstname: dep.d_firstname || null,
    d_surname: dep.d_surname || null,
    d_initial: null,
    d_dob: dep.d_dob || null,
    d_age: null,
    d_marital: dep.d_marital || null,
    d_race: dep.d_race || null,
    d_gender_radio: dep.d_gender_radio || null,
    d_rel: dep.d_rel || null,
    d_contact: dep.d_contact || null,
    d_tax: null,
    d_altemail: dep.d_altemail || null,
    d_altcontact: dep.d_altcontact || null,
    nationality: null,
    issuer_country: null,
    d_idpasstype: dep.d_idpass === 'ID Document' ? 'RSA' : (dep.d_idpass === 'Passport' ? 'PASSPORT' : null)
  })) : [];

  // --- MEMBERS ---
  // Always build members array from personal info fields
  const members = [
    {
      idpasstype: formData.idpasstype || (formData.idType === 'ID Document' ? 'RSA' : (formData.idType === 'Passport' ? 'PASSPORT' : '')),
      mainid_number: formData.mainid_number || '',
      mainid_validation: '',
      issuer_country: 'RSA',
      nationality: 'South African',
      main_title: formData.main_title || '',
      main_surname: formData.main_surname || '',
      main_firstname: formData.main_firstname || '',
      main_initial: formData.main_initial || '',
      main_marital: formData.main_marital || '',
      mgender_radio: formData.mgender_radio || '',
      m_dob: formData.m_dob || '',
      main_age: null,
      main_lang: formData.main_lang || '',
      m_race: formData.m_race || '',
      m_deps_radio: formData.registerDependants === 'Yes' ? 'yes' : 'no'
    }
  ];

  // Medical questions (from Step7MedicalHistory)
  const medical_questions = Array.isArray(formData.medical_history) ? formData.medical_history.map(cond => ({
    medical_questions: cond.question ? String(cond.question).replace('q','') : null,
    fullname: cond.fullname || null,
    illness: cond.illness || null,
    treated: cond.treated === 'Yes' ? 'Y' : (cond.treated === 'No' ? 'N' : null),
    firsttreatmentdate: cond.firsttreatmentdate || null,
    lasttreatmentdate: cond.lasttreatmentdate || null,
    gpfullname: cond.gpfullname || null,
    medication: cond.medication || null
  })) : [];

  // GP nominations, declarations, medical_schemes, members_contacts as before
  const gpnominations = Array.isArray(formData.gpnominations) ? formData.gpnominations : [];
  const declarations = Array.isArray(formData.declarations) ? formData.declarations : [
    {
      terms_agreement: null,
      advisor_dates: null,
      advisor_memfullname: null,
      mem_declaration: null,
      popi_declaration: null,
      cond_decl: null,
      medical_question_one: null,
      medical_question_two: null,
      medical_question_three: null,
      medical_question_four: null,
      condecl_confirm: null,
      broker_declare: null,
      mem_consent: null,
      broker_consent: null
    }
  ];
  const medical_schemes = [];
  const members_contacts = Array.isArray(formData.members_contacts) ? formData.members_contacts : [
    {
      m_cell: null,
      m_telh: null,
      m_telw: null,
      tax: null,
      m_comm_method: null,
      m_email: null,
      mainid_pobox: null,
      unit_num: null,
      complex_name: null,
      m_psnum: null,
      m_psname: null,
      m_pstype: null,
      m_psuburb: null,
      m_postalcode: null,
      m_ssnum: null,
      m_ssname: null,
      m_sstype: null,
      m_ssuburb: null,
      m_spostalcode: null,
      employername: null,
      employeenum: null,
      empdate: null,
      department: null,
      m_stowncode: null,
      m_towncode: null,
      m_altemail: null
    }
  ];

  return {
    advisor,
    banks_contributions,
    banks_refunds,
    boncap_incomes,
    declarations,
    dependents,
    gpnominations,
    medical_questions,
    medical_schemes,
    members,
    members_contacts
  };
}

// Utility validation functions
function isEmail(val) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
}
function isDate(val) {
  return /^\d{4}-\d{2}-\d{2}$/.test(val);
}
function isDigits(val, min=1, max=255) {
  return new RegExp(`^\\d{${min},${max}}$`).test(val);
}
function isAlphaNum(val) {
  return /^[\p{L}\s\-0-9]+$/u.test(val);
}
function isMaxLen(val, max) {
  return typeof val === 'string' && val.length <= max;
}

// Add South African ID validation function
function isValidSouthAfricanID(id) {
  if (!/^\d{13}$/.test(id)) return false; // Check for 13 digits
  // Validate date
  const yy = parseInt(id.substr(0, 2), 10);
  const mm = parseInt(id.substr(2, 2), 10);
  const dd = parseInt(id.substr(4, 2), 10);
  const dob = new Date(yy >= 50 ? 1900 + yy : 2000 + yy, mm - 1, dd);
  if (
    dob.getFullYear() !== (yy >= 50 ? 1900 + yy : 2000 + yy) ||
    dob.getMonth() + 1 !== mm ||
    dob.getDate() !== dd
  ) {
    return false;
  }
  // Validate Luhn checksum
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    let digit = parseInt(id.charAt(i), 10);
    if ((i % 2) === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return (sum % 10 === 0);
}

// Step-by-step validation
function validateStep(step, formData) {
  console.log('[validateStep] step:', step, 'formData:', formData);
  // For step 0, return an object with keys matching the field names
  if (step === 0) {
    const errors = {};
    if (!formData.medstart_date) errors.medstart_date = 'Medical start date is required.';
    if (!formData.recommendedMedicalAidPlan) errors.recommendedMedicalAidPlan = 'Please select a plan.';
    if (formData.recommendedMedicalAidPlan === 'PL470' && !formData.incomeAmount) errors.incomeAmount = 'Income amount is required for BonCap.';
    return errors;
  }
  // For step 1, return an object with field-level errors for all required fields
  if (step === 1) {
    const errors = {};
    if (!formData.idpasstype) errors.idpasstype = 'ID/Passport type is required.';
    if (!formData.main_firstname) errors.main_firstname = 'First name is required.';
    if (!formData.main_surname) errors.main_surname = 'Surname is required.';
    if (!formData.mainid_number) {
      errors.mainid_number = 'ID/Passport number is required.';
    } else if (formData.idpasstype === 'ID Document') {
      if (!/^\d{13}$/.test(formData.mainid_number)) {
        errors.mainid_number = 'South African ID must be 13 digits.';
      } else if (!isValidSouthAfricanID(formData.mainid_number)) {
        errors.mainid_number = 'Invalid South African ID number.';
      }
    }
    if (!formData.m_dob) errors.m_dob = 'Date of birth is required.';
    if (!formData.main_title) errors.main_title = 'Title is required.';
    if (!formData.main_marital) errors.main_marital = 'Marital status is required.';
    if (!formData.main_lang) errors.main_lang = 'Language is required.';
    if (!formData.m_race) errors.m_race = 'Ethnicity is required.';
    if (!formData.mgender_radio) errors.mgender_radio = 'Gender is required.';
    return errors;
  }
  // For other steps, use a local array 'stepErrorsArr' instead of 'errors'
  const stepErrorsArr = [];
  // Step 2: Contact Details
  if (step === 2) {
    const contacts = Array.isArray(formData.members_contacts) && formData.members_contacts.length > 0 ? formData.members_contacts[0] : {};
    const cellValue = contacts.m_cell || formData.m_cell || '';
    if (!cellValue || !isDigits(String(cellValue),10,10)) stepErrorsArr.push("m_cell: Valid cell number (10 digits) is required.");
    if (formData.m_email && (!isEmail(formData.m_email) || !isMaxLen(formData.m_email,255))) stepErrorsArr.push("Email must be valid and max 255 chars.");
    if (formData.m_altemail && (!isEmail(formData.m_altemail) || !isMaxLen(formData.m_altemail,255))) stepErrorsArr.push("Alt email must be valid and max 255 chars.");
    // All other fields are optional, but if filled, validate format
    [
      'unit_num','complex_name','m_psnum','m_psname','m_pstype','m_psuburb','m_postalcode',
      'm_ssnum','m_ssname','m_sstype','m_ssuburb','m_spostalcode'
    ].forEach(f => {
      if (formData[f] && !isAlphaNum(formData[f])) stepErrorsArr.push(`${f} must be alphanumeric.`);
    });
  }
  // Step 3: Dependants
  // Only validate if user selected "Yes" to register dependants AND dependants array has items
  if (step === 3 && formData.registerDependants === 'Yes' && Array.isArray(formData.dependants) && formData.dependants.length > 0) {
    formData.dependants.forEach((dep, idx) => {
      const depPrefix = `dependant${idx+1}_`;
      if (!dep.d_firstname || !isAlphaNum(dep.d_firstname) || !isMaxLen(dep.d_firstname,255)) stepErrorsArr.push(`${depPrefix}d_firstname: First name is required and must be valid.`);
      if (!dep.d_surname || !isAlphaNum(dep.d_surname) || !isMaxLen(dep.d_surname,255)) stepErrorsArr.push(`${depPrefix}d_surname: Surname is required and must be valid.`);
      if (!dep.d_idnum || !isDigits(dep.d_idnum,8,13)) stepErrorsArr.push(`${depPrefix}d_idnum: Valid ID number (8-13 digits) is required.`);
      if (!dep.d_dob || !isDate(dep.d_dob)) stepErrorsArr.push(`${depPrefix}d_dob: Date of birth (YYYY-MM-DD) is required.`);
      if (!dep.d_gender_radio || !["M","F"].includes(dep.d_gender_radio)) stepErrorsArr.push(`${depPrefix}d_gender_radio: Gender (M/F) is required.`);
      if (!dep.d_rel || !isAlphaNum(dep.d_rel) || !isMaxLen(dep.d_rel,255)) stepErrorsArr.push(`${depPrefix}d_rel: Relationship is required and must be valid.`);
      if (!dep.d_marital || !isAlphaNum(dep.d_marital) || !isMaxLen(dep.d_marital,255)) stepErrorsArr.push(`${depPrefix}d_marital: Marital status is required and must be valid.`);
      if (!dep.d_race || !isAlphaNum(dep.d_race) || !isMaxLen(dep.d_race,255)) stepErrorsArr.push(`${depPrefix}d_race: Race is required and must be valid.`);
      if (!dep.d_contact || !isDigits(dep.d_contact,10,10)) stepErrorsArr.push(`${depPrefix}d_contact: Valid contact number (10 digits) is required.`);
      if (dep.d_altcontact && !isDigits(dep.d_altcontact,10,10)) stepErrorsArr.push(`${depPrefix}d_altcontact: Alt contact must be 10 digits.`);
      if (dep.d_altemail && (!isEmail(dep.d_altemail) || !isMaxLen(dep.d_altemail,255))) stepErrorsArr.push(`${depPrefix}d_altemail: Alt email must be valid and max 255 chars.`);
    });
  }
  // Step 4: Banking Details
  if (step === 4) {
    return [];
  }
  // Step 5: Medical History
  if (step === 5) {
    return [];
  }
  return stepErrorsArr;
}

const MultiStepForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [stepErrors, setStepErrors] = useState({});

  // Load saved data and step from localStorage on mount
  React.useEffect(() => {
    const savedStep = localStorage.getItem('formStep');
    const savedData = localStorage.getItem('formData');
    console.log('[MultiStepForm] Loading from localStorage:', { savedStep, savedData });
    if (savedStep !== null && !isNaN(Number(savedStep))) {
      setActiveStep(Number(savedStep));
    }
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
    setHasLoaded(true);
  }, []);

  // Save current step and form data to localStorage whenever they change
  useEffect(() => {
    if (!hasLoaded) return;
    console.log('[MultiStepForm] Saving to localStorage:', { activeStep, formData });
    localStorage.setItem('formStep', activeStep.toString());
    localStorage.setItem('formData', JSON.stringify(formData));
  }, [activeStep, formData, hasLoaded]);

  const handleNext = () => {
    let errorsObj = {};
    if (activeStep === 0 || activeStep === 1) {
      errorsObj = validateStep(activeStep, formData);
      setStepErrors(errorsObj);
      if (Object.keys(errorsObj).length > 0) return;
    } else {
      // For Banking Details and Medical History Details steps, always proceed regardless of validation
      if (
        steps[activeStep] === "Medical History Details" ||
        steps[activeStep] === "Banking Details"
      ) {
        setStepErrors({});
        setActiveStep((prevStep) => prevStep + 1);
        return;
      }
      const errorsArr = validateStep(activeStep, formData);
      console.log('[handleNext] activeStep:', activeStep, 'errorsArr:', errorsArr);
      const errorsObjArr = {};
      if (Array.isArray(errorsArr)) {
        errorsArr.forEach(err => {
          const match = err.match(/^([A-Za-z0-9_ .'-]+):/);
          if (match) {
            const field = match[1].trim();
            errorsObjArr[field] = err.replace(/^([A-Za-z0-9_ .'-]+):/, '').trim();
          }
        });
      }
      setStepErrors(errorsObjArr);
      if (Array.isArray(errorsArr) && errorsArr.length > 0) return;
    }
    setStepErrors({});
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const calculateAge = (dobStr) => {
    if (!dobStr) return null;
    const dob = new Date(dobStr);
    if (isNaN(dob)) return null;
    const diff = Date.now() - dob.getTime();
    const ageDt = new Date(diff);
    return Math.abs(ageDt.getUTCFullYear() - 1970);
  };

  const handleSubmit = async () => {
    try {
      console.log('[handleSubmit] called');
      console.log('[handleSubmit] steps:', steps);
      // Run full validation for all steps
      let allErrors = [];
      for (let i = 0; i < steps.length; i++) {
        const stepResult = validateStep(i, formData);
        if (Array.isArray(stepResult)) {
          allErrors = allErrors.concat(stepResult);
        } else if (stepResult && typeof stepResult === 'object') {
          Object.entries(stepResult).forEach(([key, msg]) => {
            allErrors.push(`${key}: ${msg}`);
          });
        }
      }
      console.log('[handleSubmit] allErrors:', allErrors);
      const errorsObj = {};
      allErrors.forEach(err => {
        const match = err.match(/^([A-Za-z0-9_ .'-]+):/);
        if (match) {
          const field = match[1].trim();
          errorsObj[field] = err.replace(/^([A-Za-z0-9_ .'-]+):/, '').trim();
        }
      });
      console.log('[handleSubmit] errorsObj:', errorsObj);
      setStepErrors(errorsObj);
      if (allErrors.length > 0) {
        return;
      }
      setStepErrors({});
      setIsSubmitting(true);
      setSubmitError(null);
      // Enum allowed values
      const planTypeEnum = [
        "Hospital plan only",
        "Hospital plan with day-to-day benefits",
        "Hospital plan with savings"
      ];
      const planNeedsEnum = [
        "Annual day-to-day benefits",
        "Acute medicine",
        "Annual savings available",
        "Childcare",
        "Chronic medicine",
        "Dental care",
        "GP consultations",
        "Maternity cover",
        "Mental health",
        "Oncology",
        "Optical care",
        "Over-the-counter meds",
        "Physiotherapy",
        "Specialist consultations",
        "Wellness and preventative care"
      ];
      const recommendedPlanEnum = [
        "BonComprehensive",
        "BonComplete",
        "BonClassic",
        "Standard",
        "Standard Select",
        "Primary",
        "BonSave",
        "BonFit Select",
        "BonPrime",
        "BonStart",
        "BonStart Plus",
        "BonEssential",
        "BonEssential Select",
        "Hospital Standard"
      ];
      // Helper to convert camelCase to PascalCase
      const toPascalCase = str => str.charAt(0).toUpperCase() + str.slice(1);
      // Transform formData to API format
      const safeFormData = { ...formData };
      // Enum validation
      if (safeFormData.PlanTypeRequired && !planTypeEnum.includes(safeFormData.PlanTypeRequired)) {
        safeFormData.PlanTypeRequired = null;
      }
      if (safeFormData.RecommendedMedicalAidPlan && !recommendedPlanEnum.includes(safeFormData.RecommendedMedicalAidPlan)) {
        safeFormData.RecommendedMedicalAidPlan = null;
      }
      for (let i = 1; i <= 5; i++) {
        const key = `PlanNeedsRank${i}`;
        if (safeFormData[key] && !planNeedsEnum.includes(safeFormData[key])) {
          safeFormData[key] = null;
        }
      }

      // Check if this is BonCap plan
      const isBonCap = formData.recommendedMedicalAidPlan === 'PL470';
      const environment = process.env.REACT_APP_ENV || process.env.NODE_ENV;

      let agileUuid = null;
      let apiPayload = null;
      let submissionStatus = 'failed';
      let bonitasError = null;

      // 1. For Normal Plans: Submit to Agile Infinity Lead API first
      if (!isBonCap) {
        const contacts = Array.isArray(formData.members_contacts) && formData.members_contacts.length > 0 ? formData.members_contacts[0] : {};
        const agileBody = {
          FirstName: formData.main_firstname || '',
          LastName: formData.main_surname || '',
          MobilePhone: contacts.m_cell || formData.m_cell || formData.main_cell || '',
          Email: contacts.m_email || formData.m_email || formData.main_email || '',
          LeadChannel: 'Website',
          LeadProvider: 'Bonitas Website',
          SubmitProduct: 'Medical Aid',
          RSAId: formData.mainid_number || '',
          ListID: getListId(environment),
          SourceLeadID: 'Bonitas website',
          Age: calculateAge(formData.m_dob)
        };

        try {
          agileUuid = await submitAgileLead(agileBody);
        } catch (err) {
          const agileMsg = err.response?.data?.message || err.message;
          bonitasError = 'Failed to create lead in Agile Infinity: ' + agileMsg;
          setSubmitError(bonitasError);
        }
      }

      // 2. Map formData to Bonitas API structure (skip Bonitas if Agile failed for normal plans)
      if (!bonitasError) {
        if (isBonCap) {
          apiPayload = mapToBonCapDPMPayload(safeFormData);
          console.log('[BonCap] Final Payload Structure:', JSON.stringify(apiPayload, null, 2));
        } else {
          apiPayload = mapToBonitasPayload({ ...safeFormData, leadId: agileUuid });
        }

        apiPayload = {
          ...apiPayload,
          OnlineAppPayload: mapToOnlineAppPayload(safeFormData),
        };

        try {
          const response = await submitBonitasApplication(
            apiPayload,
            formData.recommendedMedicalAidPlan,
            environment
          );

          if (response.status === 200 || response.status === 201) {
            submissionStatus = 'success';
            setIsSuccess(true);
            localStorage.removeItem("formStep");
            localStorage.removeItem("formData");
          }
        } catch (error) {
          bonitasError = error.response?.data?.message || error.message;
          setSubmitError(bonitasError);
        }
      }

      try {
        await saveFormSubmission({
          status: submissionStatus,
          planCode: formData.recommendedMedicalAidPlan || null,
          agileLeadId: agileUuid,
          formData: safeFormData,
          apiPayload,
          bonitasError,
        });
      } catch (saveErr) {
        console.error('[handleSubmit] Failed to persist submission:', saveErr.message);
      } finally {
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('[handleSubmit] Exception:', err);
      setSubmitError('Unexpected error: ' + err.message);
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({});
    setIsSuccess(false);
    setSubmitError(null);
    setStepErrors({});
    // Clear saved data on reset
    localStorage.removeItem("formStep");
    localStorage.removeItem("formData");
  };

  const setFormDataWithLog = (data) => {
    const newFormData = typeof data === 'function' ? data(formData) : data;
    console.log('[MultiStepForm] setFormData called:', newFormData);
    setFormData(newFormData);
  };

  const stepProps = {
    formData,
    setFormData: setFormDataWithLog,
    errors: stepErrors,
    setErrors: setStepErrors,
    handleNext,
    handleBack,
    handleSubmit,
    handleReset,
    isSubmitting,
    submitError,
    isSuccess
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0: return <Step4PlanInfo {...stepProps} />;
      case 1: return <Step1PersonalInfo {...stepProps} />;
      case 2: return <Step3MedicalInfo {...stepProps} />;
      case 3: return <Step4Dependants {...stepProps} />;
      case 4: return <Step5BankingDetails {...stepProps} isCurrentStep />;
      case 5: return <Step7MedicalHistory {...stepProps} />;
      default: return null;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Loader overlay */}
      {isSubmitting && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(255,255,255,0.6)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress size={64} color="primary" />
        </Box>
      )}
      <Box 
        sx={{ 
          minHeight: '100vh',
          py: 0,
          backgroundColor: '#ba0d36',
          background: 'linear-gradient(45deg, #ba0d36 30%, #d13d5f 90%)',
        }}
      >
        <Container maxWidth="md" sx={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'stretch', p: 0 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 0, 
              mt: 0, 
              borderRadius: 0,
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Sticky Stepper/Header */}
            <Box sx={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', pt: 3, pb: 2 }}>
              <Stepper 
                activeStep={activeStep} 
                alternativeLabel 
                sx={{ 
                  mb: 2,
                  display: { xs: 'none', md: 'flex' },
                  '& .MuiStepIcon-root': {
                    color: '#ba0d36 !important',
                    '&.Mui-active': {
                      color: '#ba0d36 !important',
                    },
                    '&.Mui-completed': {
                      color: '#ba0d36 !important',
                    },
                  },
                  '& .MuiStepLabel-label': {
                    color: '#393636',
                    '&.Mui-active': {
                      color: '#ba0d36',
                      fontWeight: 700,
                    },
                    '&.Mui-completed': {
                      color: '#ba0d36',
                      fontWeight: 700,
                    },
                    cursor: 'pointer',
                  },
                  '& .MuiStepConnector-line': {
                    borderColor: '#ba0d36 !important',
                  },
                  '& .MuiStepConnector-active': {
                    '& .MuiStepConnector-line': {
                      borderColor: '#ba0d36 !important',
                    },
                  },
                  '& .MuiStepConnector-completed': {
                    '& .MuiStepConnector-line': {
                      borderColor: '#ba0d36 !important',
                    },
                  },
                }}
              >
          {steps.map((label, idx) => (
                  <Step key={label}>
                    <StepLabel
                      onClick={() => {
                        if (idx <= activeStep) setActiveStep(idx);
                      }}
                      style={{ cursor: idx <= activeStep ? 'pointer' : 'not-allowed' }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
          ))}
        </Stepper>
              {/* Mobile Stepper */}
              <Box 
                sx={{ 
                  display: { xs: 'block', md: 'none' },
                  mb: 2,
                  textAlign: 'center'
                }}
              >
                <Typography variant="h6" color="primary" gutterBottom>
                  Step {activeStep + 1} of {steps.length}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {steps[activeStep]}
                </Typography>
              </Box>
            </Box>

            {/* Scrollable Form Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1, sm: 4 }, py: 2 }}>
              {renderStep()}
            </Box>

            {/* Navigation Buttons inside the Paper block */}
            {activeStep !== 4 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4, mb: 2 }}>
                {activeStep > 0 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleBack}
                    sx={sharedStyles.secondaryButton}
                  >
                    Back
                  </Button>
                )}
                {/* Only show default Next button if not on Banking Details step */}
                {activeStep < steps.length - 1 && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    sx={sharedStyles.primaryButton}
                  >
                    Next
                  </Button>
                )}
                {activeStep === steps.length - 1 && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    sx={sharedStyles.submitButton}
                    type="button"
                  >
                    Submit
                  </Button>
                )}
              </Box>
            )}
            {/* Error Dialog */}
            <Dialog open={!!submitError} onClose={() => setSubmitError(null)}>
              <DialogTitle>Error</DialogTitle>
              <DialogContent>
                <Typography color="error">{submitError}</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSubmitError(null)} color="primary" autoFocus>
                  Close
                </Button>
              </DialogActions>
            </Dialog>
            {/* Success Dialog (non-dismissible) */}
            <Dialog open={isSuccess} disableEscapeKeyDown disableBackdropClick>
              <DialogTitle>Succesful Submission</DialogTitle>
              <DialogContent>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
                  Thank you, one of our consultants will be in touch.
                </Typography>
              </DialogContent>
            </Dialog>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default MultiStepForm; 